import React from 'react';
import { format } from 'date-fns';
import { formatDuration } from '../../utils/format';

export function BookingSummary({
  service,
  selectedDate,
  selectedTime,
  selectedLocation,
  peakSurcharge,
  travelFee,
  travelQuote,
  addons,
  selectedAddonIds,
  appliedDiscount,
  totalPriceBeforeDiscount,
  totalPrice,
}) {
  if (!selectedDate || !selectedTime) return null;

  return (
    <div className="mb-6 p-4 bg-[#F9F6F0] rounded-lg animate-fadeIn" data-testid="booking-summary">
      <h3 className="font-medium text-[#1A1A1A] mb-2">Booking Summary</h3>
      <div className="space-y-1 text-sm text-stone-600">
        <p className="flex justify-between">
          <span>Date:</span>
          <span className="font-medium text-[#1A1A1A]">{format(selectedDate, 'EEEE d MMMM yyyy')}</span>
        </p>
        <p className="flex justify-between">
          <span>Time:</span>
          <span className="font-medium text-[#1A1A1A]">{selectedTime}</span>
        </p>
        <p className="flex justify-between">
          <span>Duration:</span>
          <span className="font-medium text-[#1A1A1A]">{formatDuration(service.duration)}</span>
        </p>
        {selectedLocation && (
          <p className="flex justify-between" data-testid="summary-location">
            <span>Location:</span>
            <span className="font-medium text-[#1A1A1A] capitalize">
              {selectedLocation === 'salon' ? 'At salon/studio' : selectedLocation === 'home' ? "At provider's home" : 'Mobile, comes to you'}
            </span>
          </p>
        )}
        <p className="flex justify-between">
          <span>Service:</span>
          <span className="font-medium text-[#1A1A1A]">£{service.price.toFixed(2)}</span>
        </p>
        {peakSurcharge > 0 && (
          <p className="flex justify-between text-amber-600" data-testid="peak-surcharge-line">
            <span>Peak day surcharge</span>
            <span>+£{peakSurcharge.toFixed(2)}</span>
          </p>
        )}
        {travelFee > 0 && (
          <div data-testid="travel-fee-line">
            <p className="flex justify-between text-blue-600">
              <span>Mobile travel fee</span>
              <span>+£{travelFee.toFixed(2)}</span>
            </p>
            {travelQuote?.distance_miles > 0 && (
              <p className="text-xs text-blue-400 text-right">
                {travelQuote.distance_miles} miles, {travelQuote.tier ? `${travelQuote.tier.max_miles}-mile zone` : 'standard rate'}
              </p>
            )}
            {!travelQuote && selectedLocation === 'mobile' && (
              <p className="text-xs text-stone-400 text-right italic">
                Enter postcode above for exact fee
              </p>
            )}
          </div>
        )}
        {selectedAddonIds.length > 0 && addons.filter(a => selectedAddonIds.includes(a.addon_id)).map(a => (
          <p key={a.addon_id} className="flex justify-between text-xs">
            <span className="text-stone-400">+ {a.name}</span>
            <span className="text-stone-500">£{a.price.toFixed(2)}</span>
          </p>
        ))}
        {appliedDiscount && (
          <p className="flex justify-between text-green-600">
            <span>Discount ({appliedDiscount.code})</span>
            <span>-£{appliedDiscount.discount_amount.toFixed(2)}</span>
          </p>
        )}
        <div className="pt-2 mt-2 border-t border-[#E6E4DD] flex justify-between">
          <span className="font-medium">Total:</span>
          <span className="font-semibold text-[#D4AF37] text-lg">£{totalPrice.toFixed(2)}</span>
        </div>
        {service.payment_type === 'deposit_50' && (
          <div className="mt-2 p-2 bg-amber-50 rounded text-xs text-amber-700" data-testid="payment-type-info">
            <p className="font-medium">50% deposit required to book</p>
            <p className="flex justify-between mt-1">
              <span>Due now:</span>
              <span className="font-semibold">£{(totalPrice * 0.5).toFixed(2)}</span>
            </p>
            <p className="flex justify-between">
              <span>Remaining (at appointment):</span>
              <span>£{(totalPrice * 0.5).toFixed(2)}</span>
            </p>
          </div>
        )}
        {service.payment_type === 'minimum_deposit' && (
          <div className="mt-2 p-2 bg-amber-50 rounded text-xs text-amber-700" data-testid="payment-type-info">
            <p className="font-medium">Minimum deposit required to book</p>
            <p className="flex justify-between mt-1">
              <span>Due now:</span>
              <span className="font-semibold">£{Math.min(service.deposit_amount || (totalPrice * 0.2), totalPrice).toFixed(2)}</span>
            </p>
            <p className="flex justify-between">
              <span>Remaining (at appointment):</span>
              <span>£{(totalPrice - Math.min(service.deposit_amount || (totalPrice * 0.2), totalPrice)).toFixed(2)}</span>
            </p>
          </div>
        )}
        {(!service.payment_type || service.payment_type === 'full') && (
          <p className="text-xs text-stone-400 mt-1">Full payment required to confirm booking</p>
        )}
      </div>
    </div>
  );
}
